<template>
    <div class="form-wrapper">

        <!-- Success: Show credentials -->
        <div v-if="credentials">
            <h1>FormWise Access Details</h1>
            <p>Share these details with your guest so they can log in with read-only access.</p>

            <div class="credentials-box">
                <div class="credential-item">
                    <label>URL</label>
                    <div class="credential-value">{{ credentials.url }}</div>
                </div>
                <div class="credential-item">
                    <label>Email</label>
                    <div class="credential-value">{{ credentials.email }}</div>
                </div>
                <div class="credential-item">
                    <label>Password</label>
                    <div class="credential-value">{{ credentials.password }}</div>
                </div>
            </div>

            <button type="button" class="btn btn-primary w-100 mt-3" @click.prevent="copyCredentials">
                {{ copied ? 'Copied!' : 'Copy to Clipboard' }}
            </button>
            <button type="button" class="btn btn-outline-dark w-100 mt-2" @click.prevent="done">Done</button>
        </div>

        <!-- Form: Create or Edit -->
        <div v-else>
            <h1>{{ isEditing ? 'Edit Guest Access' : 'Add Guest User' }}</h1>
            <p>{{ isEditing ? 'Update the projects this guest can access.' : 'Create a guest account with read-only access to your projects.' }}</p>

            <div class="alert alert-danger p-0" v-if="error">{{ error }}</div>

            <form action="#" class="new-project-form" @submit.prevent="saveGuest">
                <div class="form-group" v-if="!isEditing">
                    <label>Name</label>
                    <input type="text" class="form-control" placeholder="Guest's full name" v-model="name">
                </div>
                <div class="form-group" v-if="!isEditing">
                    <label>Email</label>
                    <input type="email" class="form-control" placeholder="Guest's email address" v-model="email">
                </div>
                <div class="form-group" v-if="!isEditing">
                    <label>Password</label>
                    <div class="password-field">
                        <input type="text" class="form-control" v-model="password">
                        <button type="button" class="btn btn-outline-dark btn-sm btn-regenerate" @click.prevent="generatePassword">Regenerate</button>
                    </div>
                </div>
                <div class="form-group">
                    <label>Projects</label>
                    <div class="projects-checklist">
                        <div class="custom-control custom-checkbox" v-for="project in projects" :key="project.hashId">
                            <input class="custom-control-input"
                                   type="checkbox"
                                   :id="'project-' + project.hashId"
                                   :value="project.hashId"
                                   v-model="selectedProjects">
                            <label class="custom-control-label" :for="'project-' + project.hashId">{{ project.name }}</label>
                        </div>
                        <p class="text-muted small mt-1" v-if="projects.length === 0">No projects available.</p>
                    </div>
                </div>
                <div class="">
                    <button type="submit" class="btn btn-primary" style="width: 180px" :disabled="saving">
                        <span class="spinner-border" v-if="saving"></span>
                        <span :class="{'opacity-0': saving}">{{ isEditing ? 'Update Access' : 'Create Guest' }}</span>
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import repository from "@/repository/repository";

export default {
    name: "InviteGuestPopup",
    props: ['editGuest'],
    data() {
        return {
            name: "",
            email: "",
            password: "",
            selectedProjects: [],
            error: null,
            saving: false,
            credentials: null,
            copied: false,
        }
    },
    created() {
        this.generatePassword();
    },
    mounted() {
        this.$root.$on('editGuestData', (guest) => {
            this.name = guest.name;
            this.email = guest.email;
            this.selectedProjects = guest.projects.map(function (p) { return p.hashId; });
        });
    },
    beforeDestroy() {
        this.$root.$off('editGuestData');
    },
    methods: {
        generatePassword() {
            var chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
            var result = '';
            for (var i = 0; i < 12; i++) {
                result += chars.charAt(Math.floor(Math.random() * chars.length));
            }
            this.password = result;
        },
        saveGuest() {
            this.saving = true;
            this.error = null;

            if (!this.isEditing) {
                if (!this.name || this.name.trim().length < 2) {
                    this.error = "A valid name is required.";
                    this.saving = false;
                    return;
                }
                if (!this.email || !this.email.includes("@")) {
                    this.error = "A valid email is required.";
                    this.saving = false;
                    return;
                }
                if (!this.password || this.password.length < 8) {
                    this.error = "Password must be at least 8 characters.";
                    this.saving = false;
                    return;
                }
            }

            if (this.selectedProjects.length === 0) {
                this.error = "Please select at least one project.";
                this.saving = false;
                return;
            }

            if (this.isEditing) {
                repository.put("/guests/" + this.editGuest.id, {
                    project_ids: this.selectedProjects,
                })
                    .then(function () {
                        this.saving = false;
                        this.$root.$emit('closePopups');
                        this.$root.$emit('reloadGuests');
                    }.bind(this))
                    .catch(function (error) {
                        this.error = (error.response && error.response.data && error.response.data.message) || "An error occurred.";
                        this.saving = false;
                    }.bind(this))
            } else {
                repository.post("/guests", {
                    name: this.name,
                    email: this.email,
                    password: this.password,
                    project_ids: this.selectedProjects,
                })
                    .then(function (response) {
                        this.saving = false;
                        this.credentials = response.data.credentials;
                        this.$root.$emit('reloadGuests');
                    }.bind(this))
                    .catch(function (error) {
                        this.error = (error.response && error.response.data && error.response.data.message) || "An error occurred.";
                        this.saving = false;
                    }.bind(this))
            }
        },
        copyCredentials() {
            var text = 'FormWise Access Details\n' +
                'URL: ' + this.credentials.url + '\n' +
                'Email: ' + this.credentials.email + '\n' +
                'Password: ' + this.credentials.password;

            var textarea = document.createElement('textarea');
            textarea.value = text;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);

            this.copied = true;
            var self = this;
            setTimeout(function () { self.copied = false; }, 2000);
        },
        done() {
            this.$root.$emit('closePopups');
        },
        reset() {
            this.name = "";
            this.email = "";
            this.selectedProjects = [];
            this.error = null;
            this.saving = false;
            this.credentials = null;
            this.copied = false;
            this.generatePassword();
        },
    },
    computed: {
        projects() {
            return this.$store.getters.projects.items;
        },
        isEditing() {
            return this.editGuest !== null && this.editGuest !== undefined;
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../scss/variables";

.form-wrapper {
    h1 {
        font-size: 1.25rem;
        font-weight: 700;
        margin-bottom: 0.5rem;
    }

    p {
        color: $grey-text;
        margin-bottom: 1.5rem;
    }
}

.password-field {
    position: relative;

    .form-control {
        padding-right: 120px !important;
        font-family: monospace;
        letter-spacing: 0.5px;
    }

    .btn-regenerate {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: 8px;
    }
}

.credentials-box {
    background: $background-grey;
    border-radius: $box-border-radius;
    padding: 1.25rem;

    .credential-item {
        display: flex;
        align-items: baseline;
        padding: 0.4rem 0;

        &:not(:last-child) {
            border-bottom: 1px solid rgba($dark, 0.07);
        }

        label {
            font-weight: 600;
            color: $dark;
            width: 5.5rem;
            flex-shrink: 0;
            margin: 0;
            font-size: 0.9rem;
        }

        .credential-value {
            font-family: monospace;
            font-size: 0.95rem;
            color: $dark;
            word-break: break-all;
        }
    }
}

.projects-checklist {
    max-height: 200px;
    overflow-y: auto;
    padding: 0.5rem 0;

    .custom-control.custom-checkbox {
        padding-left: 0;
        margin-bottom: 0.5rem;
        cursor: pointer;

        .custom-control-label {
            padding: 0 0 0 2rem;
            font-weight: 400;
            color: $dark;
            cursor: pointer;
            display: block;
            width: 100%;

            &:before {
                top: 0;
                bottom: 0;
                margin: auto;
                left: 0;
                width: 1.5rem;
                height: 1.5rem;
                border-color: $border-grey;
            }

            &:after {
                top: 0;
                bottom: 0;
                margin: auto;
                width: 1.5rem;
                height: 1.5rem;
                left: 0;
            }
        }

        .custom-control-input ~ .custom-control-label::before {
            border: 1px solid $border-dark-grey;
        }

        .custom-control-input:checked ~ .custom-control-label::before {
            background: $primary;
            border-color: $primary;
        }
    }
}
</style>
